// What a deal write and a lead write each make stale in the other's cache.
// The lead endpoints describe the company's lead deal, a deal in the default
// pipeline, so a lead write can change that deal and a write to that deal
// can change the lead. Deals in other pipelines never reach a lead.
import type { QueryClient } from '@tanstack/react-query'
import type { Deal } from './deals'
import { refetchFocusLists, refreshLeadInLists } from './leadLists'

export const dealsKey = (companyId: string) => ['deals', companyId] as const

/**
 * After a deal is opened, changed or closed: write the returned deal into the
 * company's cached list, so the card shows the server's coerced values (a
 * date trimmed, a URL given its scheme). A deal in the default pipeline can be
 * the lead deal, so the lead card refetches and its row is patched into the
 * lists. A stage change can close or reopen the lead, so Focus refetches too.
 */
export function afterDealWrite(qc: QueryClient, deal: Deal): void {
  qc.setQueryData<{ deals: Deal[] }>(dealsKey(deal.companyId), old => {
    if (!old) return { deals: [deal] }
    const known = old.deals.some(d => d.id === deal.id)
    return { deals: known ? old.deals.map(d => (d.id === deal.id ? deal : d)) : [deal, ...old.deals] }
  })
  if (!deal.pipeline.isDefault) return
  void refreshLeadInLists(qc, deal.companyId).then(() => refetchFocusLists(qc))
}

/** After a lead write that can move its lead deal (stage, outcome, booking,
 *  claim, release, transfer, correction): refetch that company's deals. */
export function afterLeadWrite(qc: QueryClient, companyId: string): void {
  void qc.invalidateQueries({ queryKey: dealsKey(companyId), exact: true })
}
